import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Address,
  AddressWrapper,
  Price,
  Title,
  DescriptionWrapper,
  InformationWrapper,
  TitleFavWrapper,
  PriceMapIconWrapper,
  MapIconWrapper,
  DesktopUpHideWrapper,
  DesktopDownHideWrapper,
  GalleryWrapper,
  InformationsAboutProperty,
  PropertyCharacteristics,
  HousingProgramWrapper,
} from "./styles";
import { useEffect, useState } from "react";
import DescriptionTabs from "./components/DescriptionTabs";
import React from "react";
import formatValue from "../../utils/formatValue";
import { TiLocationOutline } from "react-icons/ti";
import api from "../../services/api";
import { pushViewItem } from "../../utils/dataLayer";
import LoadingComponent from "./../../components/Loading/";
import { Helmet } from "react-helmet";
import Header from "../../components/Header";
import FooterComponent from "../../components/Footer";
import RentValuesCard from "./components/RentValuesCard";
import { FiMap } from "react-icons/fi";
import { View } from "lucide-react";
import { Property } from "../../global/types";
import ErrorPropertyRented from "../../components/ErrorPropertyRented";
import PhotosGrid from "./components/PhotosGrid";
import MapSection from "./components/MapSection";
import ReactImageGallery from "react-image-gallery";
import SimilarProperties from "./components/SimilarProperties/SimilarProperties";
import PhotoCarouselSkeleton from "./PhotoCarouselSkeleton";
import { RiDragMove2Fill, RiHotelBedLine } from "react-icons/ri";
import { BiBath, BiCar } from "react-icons/bi";
import VirtualTourModal, {
  hasVirtualTourUrl,
} from "./components/VirtualTourModal";
import { formatStreetLine } from "../../utils/propertyDisplay";
import {
  buildBreadcrumbJsonLd,
  buildPropertyJsonLd,
  getPropertyDetailsPageUrl,
  isHashParam,
} from "./propertyDetailsSeo";
import PropertySeoTags from "./PropertySeoTags";
import {
  getUnavailableSimilarPropertiesSource,
  shouldShowPropertyUnavailable,
} from "./shouldShowPropertyUnavailable";
import { HousingProgramBadge } from "../../components/HousingProgramBadge";
import { getHousingProgramFromProperty } from "../../utils/housingProgram";

interface IParams {
  id: string;
}

function PropertyDetails() {
  const { id } = useParams<IParams>();

  const [loading, setLoading] = useState(true);
  const [isDetailRequestFailed, setIsDetailRequestFailed] = useState(false);
  const [fetchedProperty, setFetchedProperty] = useState({} as Property);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();
  const [carouselLoading, setCarouselLoading] = useState(true);
  const [toggleMap, setToggleMap] = useState({
    firstToggle: true,
    isActive: false,
  });
  const [isVirtualTourOpen, setIsVirtualTourOpen] = useState(false);

  const { address } = fetchedProperty;
  const streetLine = formatStreetLine(address, { includeStreetNumber: true });

  const formattedPhotos = fetchedProperty.photos?.map((photo) => ({
    original: photo.url_l,
    thumbnail: photo.url_s,
    originalAlt: `Foto do imóvel ${fetchedProperty.title}`,
    thumbnailAlt: `Miniatura do imóvel ${fetchedProperty.title}`,
  }));

  const history = useHistory();
  const location = useLocation();

  const virtualTourUrl = fetchedProperty.virtual_tour_url?.trim() ?? "";
  const showVirtualTour = hasVirtualTourUrl(virtualTourUrl);
  const housingProgram = getHousingProgramFromProperty(fetchedProperty);

  useEffect(() => {
    setLoading(true);
    setIsDetailRequestFailed(false);
    setFetchedProperty({} as Property);

    api
      .get(
        isHashParam(id)
          ? `/api/client/properties/shared/${encodeURIComponent(id)}`
          : `/api/client/properties/${parseInt(id)}`
      )
      .then((response) => {
        const property = response.data.data;

        // Child units are not public destinations — open the parent listing instead.
        if (property?.parent_property_id && !isHashParam(id)) {
          history.replace(`/imovel/${property.parent_property_id}${location.search || ""}`);
          return;
        }

        setFetchedProperty(property);
        if (property?.id) {
          pushViewItem({
            id: property.id,
            rentValue: property.rent_value,
            city: property.address?.city,
            uf: property.address?.uf,
            neighborhood: property.address?.neighborhood,
            title: property.title,
          });
        }
        const firstPhoto = property.photos?.[0];
        setThumbnailUrl(firstPhoto?.url_m || firstPhoto?.url_l || firstPhoto?.url_s);
        setLoading(false);

        setTimeout(() => {
          setCarouselLoading(false);
        }, 500);
      })
      .catch(() => {
        setIsDetailRequestFailed(true);
        setLoading(false);
      });
  }, [id, history, location.search]);

  function handleToggleMap() {
    setToggleMap((prevValue) => ({
      ...prevValue,
      isActive: !prevValue.isActive,
      firstToggle: false,
    }));
  }

  let content = <LoadingComponent />;

  function handlePhotoGridClick() {
    const { photos, title, id } = fetchedProperty;
    history.push({
      pathname: `/imovel/${id}/photos`,
      state: { photos, title },
    });
  }

  const isUnavailablePage = shouldShowPropertyUnavailable({
    isDetailRequestFailed,
    isRented: fetchedProperty.is_rented,
    isSharedHashLink: isHashParam(id),
  });

  if (!loading && isUnavailablePage) {
    content = (
      <ErrorPropertyRented
        similarRents={isDetailRequestFailed ? undefined : fetchedProperty}
        similarSource={getUnavailableSimilarPropertiesSource(
          isDetailRequestFailed
        )}
      />
    );
  } else if (!loading && fetchedProperty) {
    const { area, rooms, suites, garages, bathrooms } = fetchedProperty;

    content = (
        <>
          <PhotosGrid
            photos={fetchedProperty.photos}
            onClick={handlePhotoGridClick}
          />
          <GalleryWrapper>
            {carouselLoading ? (
              <PhotoCarouselSkeleton />
            ) : (
              <ReactImageGallery
                onClick={handlePhotoGridClick}
                items={formattedPhotos}
                showThumbnails={false}
                showBullets={true}
                showPlayButton={false}
                showFullscreenButton={false}
                lazyLoad
              />
            )}
          </GalleryWrapper>
          <InformationWrapper>
            <DescriptionWrapper>
              <TitleFavWrapper>
                <Title>{fetchedProperty.title}</Title>
              </TitleFavWrapper>

              {housingProgram && (
                <HousingProgramWrapper>
                  <HousingProgramBadge program={housingProgram} variant="prominent" />
                </HousingProgramWrapper>
              )}

              <AddressWrapper>
                <TiLocationOutline />
                <Address>
                  {[streetLine, address.neighborhood]
                    .filter(Boolean)
                    .join(" — ")}
                </Address>
              </AddressWrapper>
              <PriceMapIconWrapper>
                {!isHashParam(id) && (
                  <Price>
                    {formatValue(parseFloat(fetchedProperty.rent_value))}
                  </Price>
                )}
                {showVirtualTour && (
                  <MapIconWrapper
                    type="button"
                    onClick={() => setIsVirtualTourOpen(true)}
                  >
                    Tour Virtual <View />
                  </MapIconWrapper>
                )}
                <MapIconWrapper
                  type="button"
                  onClick={handleToggleMap}
                  className={toggleMap.isActive ? "isActive" : undefined}
                >
                  Mapa <FiMap />
                </MapIconWrapper>
              </PriceMapIconWrapper>
              <InformationsAboutProperty>
                {!!area && (
                  <div>
                    <PropertyCharacteristics>
                      <RiDragMove2Fill size={20} color="hsl(0 0% 28%)" />
                      <p>{area} m²</p>
                    </PropertyCharacteristics>
                  </div>
                )}

                {!!rooms && (
                  <div>
                    <PropertyCharacteristics>
                      <RiHotelBedLine size={20} color="hsl(0 0% 28%)" />
                      <p>{rooms} Quarto(s)</p>
                    </PropertyCharacteristics>
                  </div>
                )}

                {!!suites && (
                  <div>
                    <PropertyCharacteristics>
                      <RiHotelBedLine size={20} color="hsl(0 0% 28%)" />
                      <p>{suites} Suíte(s)</p>
                    </PropertyCharacteristics>
                  </div>
                )}

                {!!garages && (
                  <div>
                    <PropertyCharacteristics>
                      <BiCar size={20} color="hsl(0 0% 28%)" />
                      <p>{garages} Vaga(s)</p>
                    </PropertyCharacteristics>
                  </div>
                )}

                {!!bathrooms && (
                  <div>
                    <PropertyCharacteristics>
                      <BiBath size={20} color="hsl(0 0% 28%)" />
                      <p>{bathrooms} Banheiro(s)</p>
                    </PropertyCharacteristics>
                  </div>
                )}
              </InformationsAboutProperty>

              {!toggleMap.firstToggle && (
                <MapSection
                  latitude={parseFloat(fetchedProperty.address?.latitude)}
                  longitude={parseFloat(fetchedProperty.address?.longitude)}
                  address={`${streetLine},${address.neighborhood},${address.city}`}
                  toggle={toggleMap.isActive}
                />
              )}
              <DesktopUpHideWrapper>
                <RentValuesCard
                  propertyValues={fetchedProperty}
                  isScheduleDisabled={isHashParam(id)}
                />
              </DesktopUpHideWrapper>
              <DescriptionTabs property={fetchedProperty} />
            </DescriptionWrapper>
            <DesktopDownHideWrapper>
              <RentValuesCard
                propertyValues={fetchedProperty}
                isScheduleDisabled={isHashParam(id)}
              />
            </DesktopDownHideWrapper>
          </InformationWrapper>
          {fetchedProperty && (<SimilarProperties similarRents={fetchedProperty} />)}
          {showVirtualTour && (
            <VirtualTourModal
              url={virtualTourUrl}
              isOpen={isVirtualTourOpen}
              onClose={() => setIsVirtualTourOpen(false)}
            />
          )}
        </>
      );
  }

  const pageUrl = getPropertyDetailsPageUrl(id);
  const hasPropertyData =
    !isUnavailablePage && Boolean(fetchedProperty && fetchedProperty.title);

  return (
    <>
      <PropertySeoTags
        property={hasPropertyData ? fetchedProperty : null}
        pageUrl={pageUrl}
        imageUrl={thumbnailUrl}
        unavailable={!loading && isUnavailablePage}
      />
      <Helmet>
        {hasPropertyData && <meta property="og:type" content="website" />}
        {hasPropertyData && <meta property="og:locale" content="pt_BR" />}
        {hasPropertyData && (
          <script type="application/ld+json">
            {JSON.stringify(buildPropertyJsonLd(fetchedProperty, pageUrl))}
          </script>
        )}
        {hasPropertyData && (
          <script type="application/ld+json">
            {JSON.stringify(buildBreadcrumbJsonLd(fetchedProperty, pageUrl))}
          </script>
        )}
      </Helmet>
      <Header
        hasBorder
        hasSearchBar
        isToShowSearchBarMobile={false}
        hasOptionToSearch
        hideLogoMobile={true}
      />
      {content}
      <FooterComponent />
    </>
  );
}

export default PropertyDetails;